import { call, put, takeLatest } from 'redux-saga/effects';
import { addPositiveToast, addNegativeToast } from 'modules/toastContainer';
import {
  fetchChangeLog as fetchChangeLogAPI,
  fetchFeatureFlags as fetchFeatureFlagsAPI,
  saveFeatureFlag as saveFeatureFlagAPI,
} from 'api/FeatureFlagsAPI';
import { getUsers } from 'api/InternalAdminAPI';
import { addToRootSaga } from 'store';
import * as actions from './actions';
import * as actionTypes from './actionTypes';

/**
 * fetchFeatureFlags
 * Saga for fetching feature flags.
 * @return { void }
 */
export function* fetchFeatureFlags() {
  try {
    const data = yield call(fetchFeatureFlagsAPI);
    yield put(actions.fetchFeatureFlagsResponse(data));
  } catch (error) {
    yield put(actions.fetchFeatureFlagsError(error));
  }
}

/**
 * fetchChangeLog
 * Saga for fetching change log.
 * @param { String } props: Fetch props containing the feature flag name.
 * @return { void }
 */
export function* fetchChangeLog(props) {
  try {
    const data = yield call(fetchChangeLogAPI, props.featureFlag);
    yield put(actions.fetchChangeLogResponse(data));
  } catch (error) {
    yield put(actions.fetchChangeLogError(error));
  }
}

/**
 * saveFeatureFlag
 * Saga for saving feature flags.
 * @param { String } props: Feature flag and comment for change log.
 * @return { void }
 */
export function* saveFeatureFlag(props) {
  try {
    const { featureFlag, comment } = props;
    yield call(saveFeatureFlagAPI, featureFlag, comment);
    yield put(addPositiveToast('Feature flag was updated successfully'));
  } catch (error) {
    if (error?.response?.status === 400) {
      /**
       * A feature flag update may fail due to two update requests being made at the same time.
       * We should inform the user that they may need to refresh to get latest data and try again.
       */
      yield put(
        addNegativeToast(
          "Your changes couldn't be saved. A simultaneous change may have been made to this account's feature flags. Refresh the page to try again.",
        ),
      );
    } else {
      yield put(actions.saveFeatureFlagError(error));
      yield put(addNegativeToast('Failed to update the feature flag'));
    }
  }
}

export function* fetchUsersSaga() {
  try {
    const { data } = yield call(getUsers);
    yield put(actions.fetchUsersResponse(data));
  } catch (error) {
    yield put(actions.fetchUsersError(error));
  }
}

addToRootSaga([
  takeLatest(actionTypes.FETCH_CHANGE_LOG, fetchChangeLog),
  takeLatest(actionTypes.FETCH_FEATURE_FLAGS, fetchFeatureFlags),
  takeLatest(actionTypes.SAVE_FEATURE_FLAG, saveFeatureFlag),
  takeLatest(actionTypes.FETCH_USERS_REQUEST, fetchUsersSaga),
]);
